import { useState, useRef, useEffect, useMemo } from 'react'
import { GlobeEuropeAfricaIcon, XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid'
import { motion, AnimatePresence } from 'framer-motion'

export default function SharkAIChat({ AiAssistant, onClose, isMobile, buttonClassName = '', buttonFixed = true, buttonSizeClass = 'w-12 h-12', iconClassName = 'h-5 w-5' }) {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState(null)
  const chatEndRef = useRef(null)
  const questionsRef = useRef(null)

  // detect mobile if prop not provided
  const [detectedMobile, setDetectedMobile] = useState(false)
  useEffect(() => {
    const check = () => setDetectedMobile(window.innerWidth <= 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  const mobileMode = typeof isMobile === 'boolean' ? isMobile : detectedMobile

  // Prevent body scroll when open in mobileMode
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!mobileMode) return
    const original = document.body.style.overflow
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = original || ''
    }
  }, [mobileMode, isOpen])

  // Audio refs
  const popAudio = useRef(null)
  const wooshAudio = useRef(null)

  const questions = useMemo(() => [
    { id: 1, text: "What’s my day look like?", icon: "📅" },
    { id: 2, text: "My next priority?", icon: "🎯" },
    { id: 3, text: "Today's meetings?", icon: "📋" },
    { id: 4, text: "My tasks & deadlines?", icon: "✅" },
    { id: 5, text: "My shifts & hours?", icon: "⏰" },
    { id: 6, text: "Holiday & absence status?", icon: "🏖️" },
    { id: 7, text: "Team snapshot?", icon: "👥" },
    { id: 8, text: "Current Bottlenecks?", icon: "⚠️" },
    { id: 9, text: "Company News?", icon: "📊" },
    { id: 10, text: "Tomorrow's schedule?", icon: "🔮" },
  ], [])

  const memoizedMessages = useMemo(() => messages, [messages])

  const hologramVariants = {
    hidden: { opacity: 0, scale: 0.7, filter: 'blur(12px)' },
    visible: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      transition: { duration: 0.4, ease: [0.19, 1, 0.22, 1] }
    },
    exit: {
      opacity: 0,
      scale: 0.85,
      filter: 'blur(10px)',
      transition: { duration: 0.3, ease: [0.4, 0, 1, 1] }
    }
  }

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [memoizedMessages, loading])

  const handleQuestionClick = async (question) => {
    setSelectedQuestion(question)
    setMessages((prev) => [...prev, { type: 'user', text: question.text }])
    setLoading(true)
    wooshAudio.current && wooshAudio.current.play().catch(() => {})
    try {
      const response = await fetch('https://api.insightos.co.uk/api/snapshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ qId: question.id }),
      })
      const data = await response.json()
      setMessages((prev) => [
        ...prev,
        { type: 'ai', text: data.data ? data.data : 'No response from assistant.' },
      ])
      popAudio.current && popAudio.current.play().catch(() => {})
    } catch {
      setMessages((prev) => [...prev, { type: 'ai', text: 'Error contacting assistant.' }])
      popAudio.current && popAudio.current.play().catch(() => {})
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setMessages([])
    setSelectedQuestion(null)
    if (onClose) onClose()
  }

  const scrollQuestions = (direction) => {
    if (questionsRef.current) {
      const scrollAmount = 200
      questionsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`${buttonFixed ? 'fixed bottom-6 right-6 z-50' : 'relative'} ${buttonSizeClass} rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${buttonClassName}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        animate={{
          boxShadow: isOpen
            ? '0 0 30px rgba(59,130,246,0.45), 0 0 60px rgba(147,197,253,0.35)'
            : '0 10px 25px rgba(59,130,246,0.35)'
        }}
        style={{
          background: 'radial-gradient(circle at 30% 30%, #bae6fd, #2563eb)',
          border: '1px solid rgba(191,219,254,0.8)'
        }}
      >
        <GlobeEuropeAfricaIcon className={`${iconClassName} text-white`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.4 }}
              exit={{ opacity: 0 }}
              style={{
                background:
                  'radial-gradient(circle at center, rgba(191,219,254,0.35), transparent 55%)'
              }}
            />
            <motion.div
              variants={hologramVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`fixed z-50 bg-white/90 backdrop-blur-md flex flex-col overflow-hidden ${
                mobileMode
                  ? 'inset-0 w-full h-screen max-w-none rounded-none shadow-none border-none'
                  : 'bottom-6 right-6 w-96 h-[28rem] rounded-[28px] shadow-2xl border border-blue-200/60'
              }`}
              onClick={(e) => e.stopPropagation()}
              style={{
                boxShadow:
                  '0 20px 50px rgba(30,64,175,0.35), inset 0 0 40px rgba(191,219,254,0.6)',
                backgroundImage:
                  'linear-gradient(135deg, rgba(191,219,254,0.55), rgba(250,250,255,0.95))'
              }}
            >
              <motion.div
                className="absolute inset-0 pointer-events-none opacity-60"
                initial={{ backgroundPosition: '0% 0%' }}
                animate={{ backgroundPosition: '100% 100%' }}
                transition={{ duration: 6, repeat: Infinity, repeatType: 'mirror' }}
                style={{
                  backgroundImage:
                    'linear-gradient(120deg, rgba(191,219,254,0.2) 25%, transparent 25% 50%, rgba(191,219,254,0.2) 50% 75%, transparent 75%)',
                  backgroundSize: '180% 180%'
                }}
              />
        {/* Header */}
        <div className={`relative z-10 flex items-center justify-between p-3 border-b border-blue-200/60 bg-gradient-to-r from-sky-600 to-blue-700 text-white ${mobileMode ? '' : 'rounded-t-[28px]'}`}>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <GlobeEuropeAfricaIcon className="h-4 w-4" />
            </div>
            <div>
              <h1 className="font-semibold text-sm">IOS-1 Assistant</h1>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Questions Slider */}
        <div className="relative border-b border-blue-100/70 bg-gradient-to-r from-white to-blue-50">
          <button
            onClick={() => scrollQuestions('left')}
            className="absolute left-0 top-0 z-10 h-full w-8 bg-gradient-to-r from-gray-50 to-transparent flex items-center justify-center hover:from-gray-100"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <div 
            ref={questionsRef}
            className="flex overflow-x-auto scrollbar-hide px-8 py-2 space-x-2"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {questions.map((question) => (
              <button
                key={question.id}
                onClick={() => handleQuestionClick(question)}
                disabled={loading}
                className={`flex-shrink-0 flex flex-col items-center p-1.5 rounded-lg transition-all duration-200 min-w-14 ${
                  selectedQuestion?.id === question.id
                    ? 'bg-blue-100 border-2 border-blue-600'
                    : 'hover:bg-gray-100'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span className="text-sm mb-0.5">{question.icon}</span>
                <p className="text-[10px] font-medium text-gray-900 text-center leading-tight max-w-12 truncate">
                  {question.text.split(' ').slice(0, 2).join(' ')}
                </p>
              </button>
            ))}
          </div>

          <button
            onClick={() => scrollQuestions('right')}
            className="absolute right-0 top-0 z-10 h-full w-8 bg-gradient-to-l from-gray-50 to-transparent flex items-center justify-center hover:from-gray-100"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Chat Area */}
        <div className="relative z-10 flex-1 flex flex-col bg-white/80 min-h-0 backdrop-blur">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 max-h-full">
            {memoizedMessages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <GlobeEuropeAfricaIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-xs">Select a question above</p>
                </div>
              </div>
            ) : (
              memoizedMessages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-64 px-3 py-2 rounded-xl text-xs ${
                      msg.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                  </div>
                </motion.div>
              ))
            )}
            {loading && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
              >
                <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 max-w-64">
                  <div className="flex items-center space-x-1">
                    <div className="flex space-x-0.5">
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-75"></div>
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-150"></div>
                    </div>
                    <span className="text-xs text-gray-500">AI typing...</span>
                  </div>
                </div>
              </motion.div>
            )}
            <div ref={chatEndRef} />
          </div>
        </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
